import { toSignificant, toFactor } from '@/utilities/formatting';
import DataTable from '@/components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';

export interface ValuationPBRatioProps {
  uniqueSymbol: string;
  lastEarningsUpdate: number;
  reportingCurrencyISO: string;
  pbValue?: number;
  bookValuePerShare: number;
  reportingCurrencySymbol: string;
  exchangeSymbol: string;
  tickerSymbol: string;
  latestPriceDate: number;
  primaryTradingCurrencySymbol: string;
  primaryTradingCurrencyISO: string;
  primaryPrice: number;
  primaryCurrencyReported: number;
  isExchangeRateNeeded: boolean;
  exchangeRate?: number;
  industryCountryName: string;
  industrySecondaryName: string;
  industryAveragePBCount: number;
  industryAveragePB: number;
  marketCountryName: string;
  marketAveragePBCount: number;
  marketAveragePB: number;
  companyShortName: string;
}

const ValuationPBRatio = ({
  uniqueSymbol,
  lastEarningsUpdate,
  reportingCurrencyISO,
  pbValue,
  bookValuePerShare,
  reportingCurrencySymbol,
  exchangeSymbol,
  tickerSymbol,
  latestPriceDate,
  primaryTradingCurrencyISO,
  primaryPrice,
  primaryTradingCurrencySymbol,
  primaryCurrencyReported,
  isExchangeRateNeeded,
  exchangeRate,
  industryCountryName,
  industrySecondaryName,
  industryAveragePBCount,
  industryAveragePB,
  marketCountryName,
  marketAveragePBCount,
  marketAveragePB,
  companyShortName,
}: ValuationPBRatioProps) => (
  <div>
    <DataTable columnWidths="4, 4, 2" light>
      <caption>{`${uniqueSymbol} PB (Price to Book) Ratio Data Sources`}</caption>
      <thead>
        <tr>
          <th>Data Point</th>
          <th>Source</th>
          <th>Value</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Book Value per Share</td>
          <td>{`Company Filings (${new Date(
            lastEarningsUpdate
          ).toLocaleDateString()}) in ${reportingCurrencyISO}`}</td>
          <td>
            {bookValuePerShare
              ? `${reportingCurrencySymbol}${toSignificant(
                  bookValuePerShare,
                  2
                )}`
              : 'Not available'}
          </td>
        </tr>
        <tr>
          <td>{`${exchangeSymbol}:${tickerSymbol} Share Price *`}</td>
          <td>{`${exchangeSymbol} (${new Date(
            latestPriceDate
          ).toLocaleDateString()}) in ${primaryTradingCurrencyISO}`}</td>
          <td>{`${primaryTradingCurrencySymbol}${toSignificant(
            primaryPrice,
            2
          )}`}</td>
        </tr>
        {isExchangeRateNeeded && (
          <tr>
            <td>
              {`${exchangeSymbol}:${tickerSymbol} Share Price converted to ${reportingCurrencyISO} reporting currency`}
            </td>
            <td>
              {`Exchange rate (${primaryTradingCurrencyISO}/${reportingCurrencyISO}) ${toSignificant(
                exchangeRate ? exchangeRate : 0,
                3
              )}`}
            </td>
            <td>
              {`${reportingCurrencySymbol}${toSignificant(
                primaryCurrencyReported,
                2
              )}`}
            </td>
          </tr>
        )}
        {industryAveragePB && (
          <tr>
            <td>
              {`${industryCountryName} ${industrySecondaryName} Industry PB Ratio`}
            </td>
            <td>
              {`Median Figure of ${toSignificant(
                industryAveragePBCount
              )} Publicly-Listed ${industrySecondaryName} Companies`}
            </td>
            <td>{toFactor(industryAveragePB, 2)}</td>
          </tr>
        )}
        {marketAveragePB && (
          <tr>
            <td>{`${marketCountryName} Market PB Ratio`}</td>
            <td>
              {`Median Figure of ${toSignificant(
                marketAveragePBCount
              )} Publicly-Listed Companies`}
            </td>
            <td>{toFactor(marketAveragePB, 2)}</td>
          </tr>
        )}
      </tbody>
    </DataTable>
    <p> </p>
    {pbValue && (
      <DataTable columnWidths="4, 4, 2" light>
        <caption>{`${uniqueSymbol} PB (Price to Book) Ratio Calculation`}</caption>
        <thead>
          <tr>
            <th />
            <th>Calculation</th>
            <th>Outcome</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>PB Ratio</td>
            <td>
              <p>
                {`= ${exchangeSymbol}:${tickerSymbol} Share Price ÷ Book Value per Share(both in ${reportingCurrencyISO})`}
              </p>
              <p>
                {`${toSignificant(
                  primaryCurrencyReported,
                  2
                )} ÷ ${toSignificant(bookValuePerShare, 2)}`}
              </p>
            </td>
            <td>{toFactor(pbValue, 2)}</td>
          </tr>
        </tbody>
      </DataTable>
    )}
    <p>
      <sup>*</sup>
      <span>{`Primary Listing of ${companyShortName}`}</span>
    </p>
    <p>
      <span>
        {`Learn more about our ratios and growth rates in Simply Wall St’s `}
      </span>
      <a
        href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown`}
        data-unique-symbol={uniqueSymbol}
        data-about-section="valuation_pb_ratio"
        data-link-name="model_growth_rates"
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        analysis model.
      </a>
    </p>
  </div>
);

export default ValuationPBRatio;
